import React, { useCallback, useEffect, useRef, useState } from 'react';
import { CheckIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, TextField, Toggle } from './fields';
import ContactsPanel from './ContactsPanel';
import DocumentsPanel from './DocumentsPanel';
import EditHistoryLink from './EditHistoryLink';
import type { CustomDrawerProps } from './TablemaintPage';
import { titleCase } from '../../utils/titleCase';
import { formatCurrency } from '../../utils/money';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { findInvalidEmails } from '../../utils/email';
import billtosService, {
  BILLTO_DOC_TYPE,
  BilltoFormOptions,
  BilltoIntelligenceYear,
} from '../../services/billtos.service';

/**
 * Bespoke edit drawer for Bill Tos (create-locked). Most identity fields are
 * read-only display; the editable set is billing/storage settings + the Required
 * flags. Invoice Type is a tilde-delimited multiselect; soft-delete is blocked
 * while the bill-to is in use. Shows the read-only intelligence summary.
 *
 * The row from the engine list already carries every column, so we seed the form
 * from `row` directly — only options / in-use / intelligence are fetched.
 */

const TABLE = 'billtos';

// Read-only identity fields (label, key, titleCase?).
const LOCKED: { key: string; label: string; title?: boolean }[] = [
  { key: 'cname', label: 'Bill To Name', title: true },
  { key: 'mas90code', label: 'Sage Tag' },
  { key: 'mas90code_old', label: 'MAS Tag (old)' },
  { key: 'cadd1', label: 'Address 1', title: true },
  { key: 'cadd2', label: 'Address 2', title: true },
  { key: 'cadd3', label: 'Address 3', title: true },
  { key: 'ccity', label: 'City', title: true },
  { key: 'cstate', label: 'State' },
  { key: 'czip', label: 'Zip' },
  { key: 'ccountry', label: 'Country', title: true },
  { key: 'cphone', label: 'Phone' },
  { key: 'cfax', label: 'Fax' },
  { key: 'ccontact', label: 'Contact', title: true },
  { key: 'cemail', label: 'Email' },
  { key: 'curl', label: 'URL' },
  { key: 'creditterms', label: 'Credit Terms', title: true },
];

// Editable scalar fields (toggles/multiselect handled separately).
const EDITABLE_SCALAR = [
  'billtocomments', 'salesrep', 'perdiemdaystype', 'perdiemdaysfree', 'railportdaysfree',
  'perdiemadminpercent', 'perdiemadminmax', 'invoiceemail', 'storagecomments',
  'freestorageqty', 'freestorageuom', 'anniversarydays', 'frequency', 'defaultfuelcalc',
  'invoiceseparately', 'invoiceseparately_items', 'invoiceemailsubject',
];

/* How a masterpro's charges become invoices — cms.billtos.invoiceseparately
   (migrations 446/470). The default is spelled out rather than left as a bare "N":
   clearing this field must not read as "no grouping happens". */
const INVOICE_SEPARATELY = [
  { value: 'N', label: 'No — one invoice for the whole masterpro' },
  { value: 'ALWAYS', label: 'Always — main invoices and accessorials alike' },
  { value: 'MAIN', label: 'Main invoices only — accessorials still group' },
];
const YN_FIELDS = ['storagedividebycust', 'termsandconditions', 'invoiceseparately_email'];
const REQUIRED_FLAGS = [
  { key: 'requirepod', label: 'POD' },
  { key: 'requirebol', label: 'BOL' },
  { key: 'requireineir', label: 'In EIR' },
  { key: 'requireouteir', label: 'Out EIR' },
];
const ALL_DIFF_KEYS = [...EDITABLE_SCALAR, ...YN_FIELDS, ...REQUIRED_FLAGS.map((f) => f.key)];

const s = (v: any): string => (v == null ? '' : String(v));

const LockedField: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <label className="block text-sm font-medium text-muted mb-1">{label}</label>
    <div className="px-3 py-2 min-h-[38px] text-sm text-gray-800 dark:text-gray-200 bg-gray-50 dark:bg-gray-800/60 border border-gray-200 dark:border-gray-700 rounded-lg">
      {value === '' || value == null ? <span className="text-gray-400">—</span> : value}
    </div>
  </div>
);

const TextArea: React.FC<{
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
  required?: boolean;
  invalid?: boolean;
  rows?: number;
}> = ({ label, value, onChange, disabled, required, invalid, rows = 2 }) => (
  <div>
    <label className="block text-sm font-medium text-label mb-1">
      {label}
      {required && <span className="text-red-500"> *</span>}
    </label>
    <textarea
      value={value}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
      rows={rows}
      className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 dark:bg-gray-700 dark:text-white ${
        invalid
          ? 'border-red-400 dark:border-red-500 focus:ring-red-500'
          : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
      }`}
    />
  </div>
);

// Blank out legacy zero-dates for display.
const dateDisplay = (v: any): string => {
  const sv = v == null ? '' : String(v);
  return !sv || sv.startsWith('0000') ? '' : sv;
};

const BilltoEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  // Bill Tos is create-locked — the drawer only ever opens for an existing row.
  // (Guard is below the hooks so hook order stays unconditional.)
  const recno = row ? Number(row.recno) : 0;

  // The list row is slim (display columns only); fetch the full record to edit.
  const [record, setRecord] = useState<TablemaintRow>(row ?? {});
  const [form, setForm] = useState<TablemaintRow>({});
  const [invType, setInvType] = useState<string[]>([]);
  const [formDeleted, setFormDeleted] = useState(false);

  const [options, setOptions] = useState<BilltoFormOptions | null>(null);
  const [inUse, setInUse] = useState(false);
  const [intel, setIntel] = useState<BilltoIntelligenceYear[]>([]);

  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  const emailBlank = !s(form.invoiceemail).trim();
  const emailBad = findInvalidEmails(s(form.invoiceemail), { semicolon: true });
  const emailErrors = [
    ...(emailBlank ? ['Invoice Email is required.'] : []),
    ...(emailBad.length ? [`Invalid email address(es) in Invoice Email: ${emailBad.join(', ')}`] : []),
  ];
  const banner = [
    ...(showErrors ? emailErrors : []),
    ...(saveError ? [saveError] : []),
  ];

  const wasDeleted = s(record.deleted) === 'Y';

  // Fetch the full record + options + in-use + intelligence.
  useEffect(() => {
    if (!row) return;
    const ctrl = new AbortController();
    tablemaintService.get('billtos', recno).then(setRecord).catch(() => {});
    billtosService.formOptions(ctrl.signal).then(setOptions).catch(() => {});
    billtosService.context(recno, ctrl.signal).then((c) => setInUse(c.inUse)).catch(() => {});
    billtosService.intelligence(recno, ctrl.signal).then((r) => setIntel(r.years || [])).catch(() => {});
    return () => ctrl.abort();
  }, [recno]);

  // Seed the form whenever the (full) record loads/changes.
  useEffect(() => {
    const f: TablemaintRow = {};
    [...EDITABLE_SCALAR, ...YN_FIELDS, ...REQUIRED_FLAGS.map((x) => x.key)].forEach((k) => {
      f[k] = s(record[k]);
    });
    setForm(f);
    setInvType(s(record.invoicingtype).split('~').filter(Boolean));
    setFormDeleted(s(record.deleted) === 'Y');
  }, [record]);

  useEffect(() => {
    if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [saveError, showErrors]);

  const setField = (k: string, v: any) => setForm((p) => ({ ...p, [k]: v }));
  const opt = (k: keyof BilltoFormOptions) => options?.[k] ?? [];

  // "E-mail each invoice" only means anything on a bill-to that invoices separately, and
  // the server refuses the pair outright. Dropping back to N clears the flag in the same
  // setState rather than leaving it armed for a 422 the user did not cause.
  const separately = s(form.invoiceseparately) !== '' && s(form.invoiceseparately) !== 'N';
  const setSeparately = (v: string) =>
    setForm((p) => ({
      ...p,
      invoiceseparately: v || 'N',
      invoiceseparately_email: !v || v === 'N' ? 'N' : p.invoiceseparately_email,
    }));

  const toggleInv = (value: string) =>
    setInvType((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));

  const handleSave = useCallback(async () => {
    if (emailBlank || emailBad.length) {
      setShowErrors(true);
      return;
    }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      const patch: TablemaintRow = {};
      ALL_DIFF_KEYS.forEach((k) => {
        if (s(form[k]) !== s(record[k])) patch[k] = form[k];
      });
      const invJoined = invType.join('~');
      if (invJoined !== s(record.invoicingtype)) patch.invoicingtype = invJoined;
      if (formDeleted !== wasDeleted) patch.deleted = formDeleted ? 'Y' : 'N';

      if (Object.keys(patch).length === 0) {
        onClose();
        return;
      }
      await tablemaintService.update(TABLE, recno, patch);
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save.');
    } finally {
      setIsSaving(false);
    }
  }, [form, emailBlank, emailBad, invType, formDeleted, wasDeleted, record, recno, onClose, onSaved]);

  const lockedValue = (k: string, title?: boolean): React.ReactNode => {
    if (k === 'credithold') return s(record[k]) === 'Y' ? 'Yes' : 'No';
    if (k === 'arbalance') return formatCurrency(record[k]);
    const v = s(record[k]);
    return title ? titleCase(v) : v;
  };

  const selectField = (key: string, label: string, optsKey: keyof BilltoFormOptions, clearable = true) => (
    <SelectField
      key={key}
      label={label}
      disabled={isSaving}
      value={s(form[key])}
      onChange={(v) => setField(key, v)}
      options={opt(optsKey)}
      clearable={clearable}
    />
  );
  const numField = (key: string, label: string) => (
    <TextField key={key} label={label} type="number" disabled={isSaving}
      value={s(form[key])} onChange={(v) => setField(key, v)} />
  );

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (
          <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
        ) : (
          <><CheckIcon className="w-4 h-4" /> Save Changes</>
        )}
      </button>
    </div>
  );

  if (!row) return null;

  return (
    <Drawer isOpen onClose={onClose} title={`Edit Bill To — ${titleCase(s(record.cname))} (#${recno})`}
      position="right" width="56rem" footer={footer}>
      <div className="space-y-6">
        <div ref={topRef} />
        <div className="flex justify-end">
          <EditHistoryLink table="billtos" recno={recno} label={titleCase(s(record.cname))} />
        </div>
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            {banner.length === 1 ? (
              <p className="text-sm text-red-800 dark:text-red-300">{banner[0]}</p>
            ) : (
              <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
                {banner.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            )}
          </div>
        )}

        {/* Identity — read-only */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Identity (read-only)</legend>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {LOCKED.map((l) => (
              <LockedField key={l.key} label={l.label} value={lockedValue(l.key, l.title)} />
            ))}
            <LockedField label="Credit Hold" value={lockedValue('credithold')} />
            <LockedField label="A/R Balance" value={lockedValue('arbalance')} />
          </div>
        </fieldset>

        {/* Billing / storage settings — editable */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Settings</legend>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {selectField('salesrep', 'Sales Rep', 'salesreps')}
            {selectField('frequency', 'Invoice Frequency', 'frequency', false)}
            {selectField('defaultfuelcalc', 'Default Fuel Calculation', 'defaultfuelcalc', false)}
            {selectField('perdiemdaystype', 'Per Diem Days Type', 'perdiemdaystype', false)}
            {numField('perdiemdaysfree', 'Per Diem Days Free')}
            {numField('railportdaysfree', 'Rail / Port Days Free')}
            {numField('perdiemadminpercent', 'Per Diem Admin Fee %')}
            {numField('perdiemadminmax', 'Per Diem Admin Fee Max')}
            {numField('anniversarydays', 'Anniversary Days')}
            {numField('freestorageqty', 'Free Storage Qty')}
            {selectField('freestorageuom', 'Free Storage UoM', 'uom')}
          </div>

          <div className="mt-3 flex flex-wrap gap-x-8">
            <Toggle label="Divide by Customer" disabled={isSaving}
              checked={s(form.storagedividebycust) === 'Y'}
              onChange={(v) => setField('storagedividebycust', v ? 'Y' : 'N')} />
            <Toggle label="Include Terms and Conditions" disabled={isSaving}
              checked={s(form.termsandconditions) === 'Y'}
              onChange={(v) => setField('termsandconditions', v ? 'Y' : 'N')} />
          </div>
        </fieldset>

        {/* Invoice email — the addresses and what the customer sees in their inbox.
            Split out of Comments, which is where Invoice Email used to sit under a legend
            that did not describe it. */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Invoice Email</legend>
          {/* One field per row: an address list and a subject template are both long
              enough that a half-width box hides the end of what you typed. */}
          <div className="grid grid-cols-1 gap-4">
            <TextArea label="Invoice Email" required invalid={showErrors && emailBlank} disabled={isSaving}
              value={s(form.invoiceemail)} onChange={(v) => setField('invoiceemail', v)} />
            <TextField label="Email Subject Template" disabled={isSaving}
              value={s(form.invoiceemailsubject)}
              onChange={(v) => setField('invoiceemailsubject', v)} />
          </div>
          <p className="mt-2 text-xs text-muted">
            Leave the subject blank for the standard <code>Gulf Winds Invoice: {'{invoice}'}</code>.
            Placeholders: <code>{'{invoice}'}</code> the invoice number with this bill-to&apos;s
            name in place of the Sage tag, <code>{'{refs}'}</code> its Bill To reference
            numbers, <code>{'{pos}'}</code> PO numbers, <code>{'{bols}'}</code> BOL/booking
            numbers. Text inside [square brackets] is dropped when a placeholder inside it is
            empty — e.g. <code>{'Gulf Winds Invoice: {invoice}[ (PO #{pos})]'}</code> adds the
            PO only when there is one.
          </p>
        </fieldset>

        {/* Comments */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Comments</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <TextArea label="Bill To Comments" disabled={isSaving}
              value={s(form.billtocomments)} onChange={(v) => setField('billtocomments', v)} />
            <TextArea label="Storage Comments" disabled={isSaving}
              value={s(form.storagecomments)} onChange={(v) => setField('storagecomments', v)} />
          </div>
        </fieldset>

        {/* Invoice Type — tilde-delimited multiselect */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Invoice Type</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {opt('invoicingtype').map((o) => (
              <Toggle key={o.value} label={o.label} disabled={isSaving}
                checked={invType.includes(o.value)} onChange={() => toggleInv(o.value)} />
            ))}
          </div>
        </fieldset>

        {/* Invoice grouping — V1's "mass invoice" */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Invoice Grouping</legend>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <SelectField label="Invoice Separately" disabled={isSaving}
              value={s(form.invoiceseparately)}
              onChange={setSeparately}
              options={INVOICE_SEPARATELY} clearable={false} />
            <TextField label="Accessorial Line Types" disabled={isSaving}
              value={s(form.invoiceseparately_items)}
              onChange={(v) => setField('invoiceseparately_items', v)} />
          </div>
          <p className="mt-2 text-xs text-muted">
            Invoicing separately gives every set of charges its own invoice number, PDF
            and e-mail. Accessorial Line Types only applies then: an accessorial is
            invoiced only when every priced line is one of these types — otherwise it is
            left uninvoiced rather than folded in. Leave blank for no condition.
          </p>
          <div className="mt-3">
            <Toggle label="E-mail each invoice" disabled={isSaving || !separately}
              checked={s(form.invoiceseparately_email) === 'Y'}
              onChange={(v) => setField('invoiceseparately_email', v ? 'Y' : 'N')} />
            <p className="mt-1 text-xs text-muted">
              {separately
                ? 'Each invoice raised by a mass create is e-mailed to the Invoice Email '
                  + 'addresses as its own message, with its own PDF. Leave off to raise the '
                  + 'invoices without sending anything.'
                : 'Only available when this bill-to invoices separately.'}
            </p>
          </div>
        </fieldset>

        {/* Required documents/flags */}
        <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
          <legend className="px-2 text-sm font-semibold text-label">Required</legend>
          <div className="flex flex-wrap gap-x-6 gap-y-1">
            {REQUIRED_FLAGS.map((f) => (
              <Toggle key={f.key} label={f.label} disabled={isSaving}
                checked={s(form[f.key]) === 'Y'}
                onChange={(v) => setField(f.key, v ? 'Y' : 'N')} />
            ))}
          </div>
        </fieldset>

        {/* Contacts + Documents */}
        <ContactsPanel tablename="billtos" recno={recno} />
        <DocumentsPanel recno={recno} basePath="/billtos" docType={BILLTO_DOC_TYPE} />

        {/* Intelligence — read-only */}
        {intel.length > 0 && (
          <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <legend className="px-2 text-sm font-semibold text-label">Intelligence</legend>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-muted">
                  <th className="py-1 font-medium">Year</th>
                  <th className="py-1 font-medium text-right">Volume</th>
                  <th className="py-1 font-medium text-right">Revenue</th>
                  <th className="py-1 font-medium text-right">Margin</th>
                </tr>
              </thead>
              <tbody>
                {intel.map((y) => (
                  <tr key={y.year} className="text-gray-800 dark:text-gray-200">
                    <td className="py-1">{y.year}</td>
                    <td className="py-1 text-right">{y.volume.toLocaleString()}</td>
                    <td className="py-1 text-right">{formatCurrency(y.revenue)}</td>
                    <td className="py-1 text-right">{y.margin == null ? '—' : `${y.margin}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </fieldset>
        )}

        {/* Created / Last Used — read-only, just above the delete flag */}
        <div className="grid grid-cols-2 gap-3">
          <LockedField label="Created" value={dateDisplay(record.createdatetime)} />
          <LockedField label="Last Used" value={dateDisplay(record.lastuseddatetime)} />
        </div>

        {/* Status — soft-delete (blocked while in use) */}
        <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
          <Toggle label="Deleted" checked={formDeleted} disabled={isSaving || (inUse && !formDeleted)}
            onChange={setFormDeleted} />
          <p className="mt-1 ml-7 text-xs text-muted">
            {inUse && !wasDeleted
              ? 'In use by an open receiving or pending change — can’t be deleted right now.'
              : 'Soft-deletes the bill-to — hidden from filters but preserved in history. Uncheck and save to restore.'}
          </p>
        </div>
      </div>
    </Drawer>
  );
};

export default BilltoEditDrawer;
